import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { Permission, hasPermission, getRolePermissions, canPerformAction, getRoleDisplayName, getRoleDescription } from '../features/shared/permissions';
import { UserRole } from '../types/user';

export interface PermissionHook {
  // Permission checking functions
  hasPermission: (permission: Permission) => boolean;
  canPerformAction: (resource: string, action: string) => boolean;
  
  // Role information
  userRole: UserRole | null;
  isAdmin: boolean;
  isDispatch: boolean;
  isCFI: boolean;
  roleDisplayName: string | null;
  roleDescription: string | null;
  userPermissions: Permission[];
  
  // Organization context
  isFlightSchool: boolean;
  organizationType: string | null;
  
  // Utility functions
  canManageMembers: boolean;
  canManageAircraft: boolean;
  canManageBookings: boolean;
  canManageMaintenance: boolean;
  canCreateFlightPlans: boolean;
  canApproveFlightPlans: boolean;
  canManageAssignments: boolean;
  canManageTraining: boolean;
  canDispatch: boolean;
}

export function usePermissions(): PermissionHook {
  const { currentUser } = useAuth();
  const { organization } = useOrganization();
  
  const userRole = currentUser?.role || null;
  const organizationType = organization?.type ?? null;
  const isFlightSchool = organizationType === 'school';

  // Role flags (same pattern as useUser) for convenience when using usePermissions
  const isAdmin = userRole === 'admin';
  const isDispatch = userRole === 'dispatch';
  const isCFI = userRole === 'cfi';

  const userPermissions = useMemo(() => {
    if (!userRole || !organizationType) return [];
    return getRolePermissions(organizationType, userRole);
  }, [userRole, organizationType]);
  
  const roleDisplayName = useMemo(() => {
    if (!userRole) return null;
    return getRoleDisplayName(userRole);
  }, [userRole]);
  
  const roleDescription = useMemo(() => {
    if (!userRole) return null;
    return getRoleDescription(userRole);
  }, [userRole]);
  
  // Permission checking functions
  const checkPermission = (permission: Permission): boolean => {
    if (!userRole || !organizationType) return false;
    return hasPermission(organizationType, userRole, permission);
  };

  const checkAction = (resource: string, action: string): boolean => {
    if (!userRole || !organizationType) return false;
    return canPerformAction(organizationType, userRole, resource, action);
  };
  
  // Convenience permission checks
  const canManageMembers = checkPermission('MEMBERS:ASSIGN_ROLES');
  const canManageAircraft = checkPermission('AIRCRAFT:CREATE');
  const canManageBookings = checkPermission('BOOKINGS:DISPATCH_OVERRIDE');
  const canManageMaintenance = checkPermission('MAINTENANCE:APPROVE_WORK');
  const canCreateFlightPlans = checkPermission('FLIGHT_PLANS:CREATE');
  const canApproveFlightPlans = checkPermission('FLIGHT_PLANS:APPROVE');
  const canManageAssignments = checkPermission('ASSIGNMENTS:CREATE');
  const canManageTraining = checkPermission('TRAINING:ENDORSE');
  const canDispatch = checkPermission('BOOKINGS:DISPATCH_OVERRIDE');
  
  return {
    hasPermission: checkPermission,
    canPerformAction: checkAction,
    userRole,
    isAdmin,
    isDispatch,
    isCFI,
    roleDisplayName,
    roleDescription,
    userPermissions,
    isFlightSchool,
    organizationType: organization?.type || null,
    canManageMembers,
    canManageAircraft,
    canManageBookings,
    canManageMaintenance,
    canCreateFlightPlans,
    canApproveFlightPlans,
    canManageAssignments,
    canManageTraining,
    canDispatch
  };
}
